<template>
  <div>
    <div class="picker">
      <label :for="model.woj.$key">{{ model.woj.$schema.title }}</label>

      <v-select
        :class="{'is-invalid': this.model.woj.$error}"
        :inputId="model.woj.$key"
        :options="wojs"
        :value="wojValue"
        @search:blur="blur('woj')"
        @search:focus="focus('woj')"
        @input="selectedWoj"
      >
        <template v-slot:no-options>
          {{ emptyListMsg }}
        </template>
      </v-select>

      <w-error v-if="showError" :model="this.model.woj"></w-error>
    </div>

    <div class="picker">
      <label :for="model.pow.$key">{{ model.pow.$schema.title }}</label>

      <v-select
        :class="{'is-invalid': this.model.pow.$error}"
        :disabled="powDisabled"
        :inputId="model.pow.$key"
        :options="pows"
        :value="powValue"
        @search:blur="blur('pow')"
        @search:focus="focus('pow')"
        @input="selectedPow"
      >
        <template v-slot:no-options>
          {{ emptyListMsg }}
        </template>
      </v-select>

      <w-error v-if="showError" :model="this.model.pow"></w-error>
    </div>

    <div class="picker">
      <label :for="model.gmi.$key">{{ model.gmi.$schema.title }}</label>

      <v-select
        :class="{'is-invalid': this.model.gmi.$error}"
        :disabled="gmiDisabled"
        :inputId="model.gmi.$key"
        :options="gmis"
        :value="gmiValue"
        @search:blur="blur('gmi')"
        @search:focus="focus('gmi')"
        @input="selectedGmi"
      >
        <template v-slot:no-options>
          {{ emptyListMsg }}
        </template>
      </v-select>

      <w-error v-if="showError" :model="this.model.gmi"></w-error>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import WError from './error.vue';
import {TercType} from '../enums/tercType.enum';

// @vuese
// Input na TERC-a
// @group Components
export default {
  name: 'w-terc-picker',
  components: {vSelect, WError},
  props: {
    // URL do TERYT API
    url: {
      type: String,
      required: false,
      default: 'https://teryt.api.witkac.pl'
    },
    // Schema
    model: {
      type: Object,
      required: true
    },
    // Tekst do wyświetlenia w przypadku pustej listy
    emptyListMsg: {
      type: String,
      required: false,
      default: ''
    },
    // Czy pokazywać błędy
    showError: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      wojs: [],
      pows: [],
      gmis: [],
      wojValue: undefined,
      powValue: undefined,
      gmiValue: undefined,
      woj: '',
      pow: '',
      gmi: ''
    };
  },
  computed: {
    powDisabled() {
      return !this.wojValue ? true : false;
    },
    gmiDisabled() {
      return !this.powValue ? true : false;
    }
  },
  watch: {
    'model.terc.$value'(val) {
      this.setTercData(val);
    },
    wojValue(newValue) {
      if (newValue) {
        this.getPowList();
      }
    },
    powValue(newValue) {
      if (newValue) {
        this.getGmiList();
      }
    }
  },
  methods: {
    // @vuese
    // Blur na polu schemy
    // @arg `field` - nazwa pola schemy
    blur(field) {
      this.model[field].$hasFocus = false;
    },
    // @vuese
    // Focus na polu schemy
    // @arg `field` - nazwa pola schemy
    focus(field) {
      this.model[field].$hasFocus = true;
    },
    // @vuese
    // Metoda pobiera listę województw
    getWojList() {
      axios
        .get(`${this.url}/terc/?type=${TercType.WOJEWODZTWO}`)
        .then(res => {
          this.wojs = res.data.list.map(item => ({value: item.woj, label: item.name.toLowerCase()}));
        })
        .catch(error => this.$emit('error', 'Nie udało się pobrać listy województw'));
    },
    // @vuese
    // Metoda pobiera listę powiatów
    getPowList() {
      if (this.wojValue) {
        axios
          .get(`${this.url}/terc/?type=${TercType.POWIAT}&woj=${this.wojValue.value}`)
          .then(res => {
            this.pows = res.data.list.map(item => ({value: item.pow, label: item.name}));
          })
          .catch(error => this.$emit('error', 'Nie udało się pobrać listy powiatów'));
      }
    },
    // @vuese
    // Metoda pobiera listę gmin
    getGmiList() {
      if (this.wojValue && this.powValue) {
        axios
          .get(`${this.url}/terc/?type=${TercType.GMINA}&woj=${this.wojValue.value}&pow=${this.powValue.value}`)
          .then(res => {
            this.gmis = res.data.list.map(item => ({value: item.gmi, label: item.nameFull}));
            if (this.gmis.length === 1 && !this.gmiValue) {
              this.selectedGmi(this.gmis[0]);
            }
          })
          .catch(error => this.$emit('error', 'Nie udało się pobrać listy gmin'));
      }
    },
    // @vuese
    // Wyczyszczenie odpowiednich wartosci po wybraniu województwa oraz zapis do schemy
    // @arg `val` - obiekt z nazwą oraz symbolem województwa
    selectedWoj(val) {
      this.$set(this, 'wojValue', val);
      this.cleanValues(['pow', 'gmi']);

      this.woj = val ? val.value : '';
      this.model.$setData({
        woj: val ? val.label : '',
        pow: '',
        gmi: '',
        terc: `${this.woj}`
      });
    },
    // @vuese
    // Wyczyszczenie odpowiednich wartości po wybraniu powiatu oraz zapis do schemy
    // @arg `val` - obiekt z nazwą oraz symbolem powiatu
    selectedPow(val) {
      this.$set(this, 'powValue', val);
      this.cleanValues(['gmi']);

      this.pow = val ? val.value : '';
      this.model.$setData({
        pow: val ? val.label : '',
        gmi: '',
        terc: `${this.woj}${this.pow}`
      });
    },
    // @vuese
    // Zapis do schemy
    // @arg `val` - obiekt z nazwą oraz symbolem gminy
    selectedGmi(val) {
      this.$set(this, 'gmiValue', val);

      this.gmi = val ? val.value : '';
      this.model.$setData({
        gmi: val ? val.label : '',
        terc: `${this.woj}${this.pow}${this.gmi}`
      });
    },
    // @vuese
    // Czyści odpowiednie wartości
    // @arg `types` - tablica nazw wartości do wyczyszczenia: 'woj', 'pow', 'gmi'
    cleanValues(types) {
      types.forEach(type => {
        this.$set(this, `${type}Value`, undefined);
        this.$set(this, `${type}s`, []);
      });
    },
    // @vuese
    // Ustawianie pól formularza
    // @arg `tercValue` - kod TERC
    setTercData(tercValue) {
      const woj = tercValue.slice(0, 2);
      const pow = tercValue.slice(2, 4);
      const gmi = tercValue.slice(4, 6);

      this.findWoj(woj);
      this.findPow(woj, pow);
      this.findGmi(woj, pow, gmi);
    },
    // @vuese
    // Pobiera województwo
    // @arg `woj` - kod województwa
    findWoj(woj) {
      if (woj) {
        axios
          .get(`${this.url}/terc/?type=${TercType.WOJEWODZTWO}&woj=${woj}`)
          .then(res => this.$set(this, 'wojValue', res.data.list.map(item => ({value: item.woj, label: item.name.toLowerCase()}))[0]))
          .catch(error => this.$emit('error', 'Nie udało się pobrać województwa'));
      }
    },
    // @vuese
    // Pobiera powiat
    // @arg `woj` - kod województwa
    // @arg `pow` - kod powiatu
    findPow(woj, pow) {
      if (woj && pow) {
        axios
          .get(`${this.url}/terc/?type=${TercType.POWIAT}&woj=${woj}&pow=${pow}`)
          .then(res => this.$set(this, 'powValue', res.data.list.map(item => ({value: item.pow, label: item.name}))[0]))
          .catch(error => this.$emit('error', 'Nie udało się pobrać powiatu'));
      }
    },
    // @vuese
    // Pobiera gminę
    // @arg `woj` - kod województwa
    // @arg `pow` - kod powiatu
    // @arg `gmi` - kod gminy
    findGmi(woj, pow, gmi) {
      if (woj && pow && gmi) {
        axios
          .get(`${this.url}/terc/?type=${TercType.GMINA}&woj=${woj}&pow=${pow}&gmi=${gmi}`)
          .then(res => this.$set(this, 'gmiValue', res.data.list.map(item => ({value: item.gmi, label: item.nameFull}))[0]))
          .catch(error => this.$emit('error', 'Nie udało się pobrać gminy'));
      }
    }
  },
  beforeMount() {
    this.getWojList();

    if (this.model.terc) {
      this.setTercData(this.model.terc.$value);
    }
  }
};
</script>

<style>
.picker {
  margin-bottom: 25px;
}

.is-invalid .vs__dropdown-toggle {
  border-color: #fd3995;
}
</style>
